import React, { useState, useEffect } from 'react';
import { ConsentPreferences, getConsentPreferences, saveConsentPreferences } from '../utils/consentManager';

interface CookiePreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (preferences: Omit<ConsentPreferences, 'timestamp'>) => void;
}

const CookiePreferencesModal: React.FC<CookiePreferencesModalProps> = ({ isOpen, onClose, onSave }) => {
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    if (isOpen) {
      // Load current preferences when modal opens
      const current = getConsentPreferences();
      if (current) {
        setPreferences({
          essential: current.essential,
          analytics: current.analytics,
          marketing: current.marketing,
        });
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    saveConsentPreferences(preferences);
    onSave(preferences);
    onClose();
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: false, // Set true if you use marketing cookies
    };
    setPreferences(allAccepted);
    saveConsentPreferences(allAccepted);
    onSave(allAccepted);
    onClose();
  };

  const handleRejectAll = () => {
    const allRejected = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    setPreferences(allRejected);
    saveConsentPreferences(allRejected);
    onSave(allRejected);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal */}
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-modal-title"
      >
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <h2 id="cookie-modal-title" className="text-2xl font-bold text-gray-900 dark:text-white">
                🍪 Cookie Preferences
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl leading-none"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-6">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              We use cookies to enhance your experience on our website. You can choose which categories of cookies to accept.
              Essential cookies are always active as they are necessary for the site to function.
            </p>

            {/* Essential Cookies */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Essential Cookies
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    These cookies are necessary for the website to function properly. They enable core functionality
                    such as security, network management, and accessibility. These cookies cannot be disabled.
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    Examples: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">cc_consent</code>,{' '}
                    <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">theme</code>,{' '}
                    <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">sessionWarningDismissed</code>
                  </p>
                </div>
                <div className="ml-4">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded cursor-not-allowed opacity-50"
                    aria-label="Essential cookies (always enabled)"
                  />
                </div>
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Analytics Cookies
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    These cookies help us understand how visitors interact with our website by collecting and
                    reporting information anonymously. We use Google Analytics for this purpose.
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    Examples: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">_ga</code>,{' '}
                    <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">_gid</code>,{' '}
                    <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">_gat</code>
                  </p>
                </div>
                <div className="ml-4">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                    aria-label="Toggle analytics cookies"
                  />
                </div>
              </div>
            </div>

            {/* Marketing Cookies */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 opacity-60">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Marketing Cookies
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    These cookies are used to track visitors across websites to display relevant advertisements.
                    <strong className="block mt-1">We currently do not use marketing cookies.</strong>
                  </p>
                </div>
                <div className="ml-4">
                  <input
                    type="checkbox"
                    checked={false}
                    disabled
                    className="w-5 h-5 text-gray-400 bg-gray-100 border-gray-300 rounded cursor-not-allowed opacity-50"
                    aria-label="Marketing cookies (not used)"
                  />
                </div>
              </div>
            </div>

            <div className="pt-2">
              <a
                href="/cookie-policy"
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                onClick={onClose}
              >
                Read our full Cookie Policy →
              </a>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button
                onClick={handleRejectAll}
                className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
              >
                Reject All
              </button>
              <button
                onClick={handleSave}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Save Preferences
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookiePreferencesModal;
